import type { Chat } from "@/types/chat"

import type { AppDispatch, RootState } from "./redux-store"
import { setChatTabResumeChatId } from "./site-nav-slice"
import { selectChatId, selectHasPersistentMessages } from "./user-slice"
import {
  selectVoiceModeChatId,
  selectVoiceModeUserSpoke,
} from "./voice-chat-slice"

/** True when the chat has user-visible conversation (not an empty new chat). */
export function chatHasConversation(
  chat: Pick<Chat, "messages" | "log_items">
): boolean {
  return chat.messages.length > 0 || chat.log_items.length > 0
}

/** User opened or continued a real conversation; mobile Chat tab resumes this chat. */
export function markChatTabEngaged(dispatch: AppDispatch, chatId: number) {
  dispatch(setChatTabResumeChatId(chatId))
}

/** Call after loading a chat from the API. */
export function markChatTabEngagedFromChat(
  dispatch: AppDispatch,
  chat: Pick<Chat, "id" | "messages" | "log_items">
) {
  if (chatHasConversation(chat)) {
    markChatTabEngaged(dispatch, chat.id)
  }
}

/**
 * Ending a voice call never navigates, but a call the customer actually spoke
 * in is a real conversation, so it gets the same mark a text exchange leaves.
 * A silent call persisted nothing (the greeting is voice-only) and stays
 * unmarked. Pass state from BEFORE the close — voiceModeClosed clears the
 * call's chat id.
 */
export function markCompletedVoiceChat(
  dispatch: AppDispatch,
  state: RootState
) {
  const chatId = selectVoiceModeChatId(state)
  if (chatId !== null && selectVoiceModeUserSpoke(state)) {
    markChatTabEngaged(dispatch, chatId)
  }
}

/** Call after Redux already reflects a loaded conversation. */
export function markChatTabEngagedFromState(
  dispatch: AppDispatch,
  getState: () => RootState
) {
  const chatId = selectChatId(getState())
  if (chatId > 0 && selectHasPersistentMessages(getState())) {
    markChatTabEngaged(dispatch, chatId)
  }
}
